import React, { useState } from 'react';
import { publicFetch, apiEndpoints } from '../config/api';
import { FiSearch } from 'react-icons/fi';

const UserBookingStatus = () => {
  const [searchMethod, setSearchMethod] = useState('email');
  const [searchValue, setSearchValue] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setSearched(true);

    try {
      let response;
      
      if (searchMethod === 'email') {
        // ✅ Use the API endpoints from config
        const url = `${apiEndpoints.bookings.getAll}/user?email=${encodeURIComponent(searchValue)}`;
        console.log('🔍 Fetching bookings from:', url);
        
        response = await publicFetch(url);
        const data = await response.json();
        console.log('📊 Response:', data);
        
        setBookings(data.bookings || []);
      } else {
        // Search by booking ID
        console.log('🔍 Fetching booking by ID:', searchValue);
        response = await publicFetch(`${apiEndpoints.bookings.getAll}/${searchValue}`);
        const data = await response.json();
        console.log('📊 Response:', data);
        
        if (data.booking) {
          setBookings([data.booking]);
        } else {
          setBookings([]);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching bookings:', error);
      setError(error.response?.data?.message || 'Booking not found. Please check your information and try again.');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return {
          className: 'ui-mdc-booking-status-banner ',
          icon: '⏳',
          message: 'Your booking is pending admin confirmation. You will receive an email once it\'s confirmed.',
        };
      case 'confirmed':
        return {
          className: 'ui-mdc-booking-status-banner ui-mdc-booking-status-banner--confirmed',
          icon: '✅',
          message: 'Your booking is confirmed! We look forward to your visit.',
        };
      case 'cancelled':
        return {
          className: 'ui-mdc-booking-status-banner ui-mdc-booking-status-banner--cancelled',
          icon: '❌',
          message: 'This booking has been cancelled.',
        };
      case 'completed':
        return {
          className: 'ui-mdc-booking-status-banner ui-mdc-booking-status-banner--completed',
          icon: '✓',
          message: 'This booking has been completed. Thank you for choosing us!',
        };
      default:
        return {
          className: 'ui-mdc-booking-status-banner ui-mdc-booking-status-banner--unknown',
          icon: '?',
          message: 'Status unknown',
        };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const timeSlotDisplay = {
    morning: 'Morning Safari (5:30 AM - 9:30 AM)',
    afternoon: 'Afternoon Safari (2:30 PM - 6:30 PM)',
    extended: 'Extended Safari (5:30 AM - 12:00 PM)',
    fullDay: 'Full Day Safari (5:30 AM - 6:30 PM)'
  };

  return (
    <div className="ui-mdc-booking-status-page">
      <div className="ui-mdc-booking-status-container">
        {/* Header */}
        <div className="ui-mdc-booking-status-header">
          <div className="ui-mdc-booking-status-kicker mt-5">
            BOOKING TRACKER
          </div>
          <h1 className="ui-mdc-booking-status-title">Check Your Booking Status</h1>
          <p className="ui-mdc-booking-status-subtitle">Track your safari booking in real-time</p>
        </div>

        {/* Search Form */}
        <div className="ui-mdc-booking-status-card">
          <div className="ui-mdc-booking-status-search-method">
            <label className="ui-mdc-booking-status-label">
              Search by:
            </label>
            <div className="ui-mdc-booking-status-method-buttons">
              <button
                type="button"
                onClick={() => setSearchMethod('email')}
                className={`ui-mdc-booking-status-method-btn ${
                  searchMethod === 'email' ? 'ui-mdc-booking-status-method-btn--active' : ''
                }`}
              >
                Email Address
              </button>
              {/* <button
                type="button"
                onClick={() => setSearchMethod('booking')}
                className={`ui-mdc-booking-status-method-btn ${
                  searchMethod === 'booking' ? 'ui-mdc-booking-status-method-btn--active' : ''
                }`}
              >
                Booking ID
              </button> */}
            </div>
          </div>

          <form onSubmit={handleSearch} className="ui-mdc-booking-status-form">
            <div className="ui-mdc-booking-status-input-group">
              <input
                type={searchMethod === 'email' ? 'email' : 'text'}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={
                  searchMethod === 'email'
                    ? 'Enter your email address'
                    : 'Enter your booking ID (e.g., YALA-xxxxx)'
                }
                className="ui-mdc-booking-status-input"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="ui-mdc-booking-status-submit"
            >
              {loading ? (
                <>
                  <div className="ui-mdc-booking-status-spinner"></div>
                  Searching...
                </>
              ) : (
                <>
                  <FiSearch /> Check Status
                </>
              )}
            </button>
          </form>

          {error && (
            <div className="ui-mdc-booking-status-error">
              {error}
            </div>
          )}
        </div>

        {/* Results */}
        {searched && !loading && bookings.length === 0 && !error && (
          <div className="ui-mdc-booking-status-empty">
            <div className="ui-mdc-booking-status-empty-icon">
              <FiSearch />
            </div>
            <h3 className="ui-mdc-booking-status-empty-title">No Bookings Found</h3>
            <p className="ui-mdc-booking-status-empty-text">
              {searchMethod === 'email'
                ? 'No bookings found with this email address.'
                : 'No booking found with this ID.'}
            </p>
          </div>
        )}

        {bookings.length > 0 && (
          <div className="ui-mdc-booking-status-results">
            {bookings.map((booking) => {
              const statusInfo = getStatusInfo(booking.status);
              return (
                <div key={booking._id} className="ui-mdc-booking-status-item">
                  {/* Status Banner */}
                  <div className={statusInfo.className}>
                    <div className="ui-mdc-booking-status-banner-content">
                      <span className="ui-mdc-booking-status-banner-icon">{statusInfo.icon}</span>
                      <div className="ui-mdc-booking-status-banner-text">
                        <h3 className="ui-mdc-booking-status-banner-title">
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </h3>
                        <p className="ui-mdc-booking-status-banner-message">{statusInfo.message}</p>
                      </div>
                    </div>
                  </div>

                  {/* Booking Details */}
                  <div className="ui-mdc-booking-status-details">
                    <div className="ui-mdc-booking-status-header-info">
                      <div>
                        <h4 className="ui-mdc-booking-status-id">{booking.bookingId}</h4>
                        <p className="ui-mdc-booking-status-date">
                          Booked on: {new Date(booking.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="ui-mdc-booking-status-price">
                        <p className="ui-mdc-booking-status-price-amount">
                          ${booking.totalPrice.toFixed(2)}
                        </p>
                        <p className="ui-mdc-booking-status-price-label">Total Amount</p>
                      </div>
                    </div>

                    <div className="ui-mdc-booking-status-info-grid">
                      <div className="ui-mdc-booking-status-info-card">
                        <h5 className="ui-mdc-booking-status-info-title">Safari Details</h5>
                        <div className="ui-mdc-booking-status-info-list">
                          <p className="ui-mdc-booking-status-info-item">
                            <span className="ui-mdc-booking-status-info-label">Date:</span>
                            <span className="ui-mdc-booking-status-info-value">{formatDate(booking.date)}</span>
                          </p>
                          <p className="ui-mdc-booking-status-info-item">
                            <span className="ui-mdc-booking-status-info-label">Time:</span>
                            <span className="ui-mdc-booking-status-info-value">{timeSlotDisplay[booking.timeSlot]}</span>
                          </p>
                          <p className="ui-mdc-booking-status-info-item">
                            <span className="ui-mdc-booking-status-info-label">People:</span>
                            <span className="ui-mdc-booking-status-info-value">{booking.people}</span>
                          </p>
                          <p className="ui-mdc-booking-status-info-item">
                            <span className="ui-mdc-booking-status-info-label">Park:</span>
                            <span className="ui-mdc-booking-status-info-value capitalize">{booking.park || 'N/A'}</span>
                          </p>
                        </div>
                      </div>

                      <div className="ui-mdc-booking-status-info-card">
                        <h5 className="ui-mdc-booking-status-info-title">Customer Info</h5>
                        <div className="ui-mdc-booking-status-info-list">
                          <p className="ui-mdc-booking-status-info-item">
                            <span className="ui-mdc-booking-status-info-label">Name:</span>
                            <span className="ui-mdc-booking-status-info-value">{booking.customerName}</span>
                          </p>
                          <p className="ui-mdc-booking-status-info-item">
                            <span className="ui-mdc-booking-status-info-label">Email:</span>
                            <span className="ui-mdc-booking-status-info-value">{booking.customerEmail}</span>
                          </p>
                          <p className="ui-mdc-booking-status-info-item">
                            <span className="ui-mdc-booking-status-info-label">Phone:</span>
                            <span className="ui-mdc-booking-status-info-value">{booking.customerPhone}</span>
                          </p>
                          <p className="ui-mdc-booking-status-info-item">
                            <span className="ui-mdc-booking-status-info-label">Payment:</span>
                            <span className={`ui-mdc-booking-status-payment-badge ui-mdc-booking-status-payment-badge--${booking.paymentStatus}`}>
                              {booking.paymentStatus.toUpperCase()}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {booking.adminNotes && (
                      <div className="ui-mdc-booking-status-notes">
                        <p className="ui-mdc-booking-status-notes-title">📝 Admin Notes:</p>
                        <p className="ui-mdc-booking-status-notes-text">{booking.adminNotes}</p>
                      </div>
                    )}

                    {booking.status === 'pending' && (
                      <div className="ui-mdc-booking-status-next">
                        <h5 className="ui-mdc-booking-status-next-title">📞 What's Next?</h5>
                        <ul className="ui-mdc-booking-status-next-list">
                          <li>Our team is reviewing your booking request</li>
                          <li>You will receive a confirmation email within 24 hours</li>
                          <li>Please keep your booking ID for reference</li>
                          <li>For urgent queries, contact us via WhatsApp</li>
                        </ul>
                      </div>
                    )}

                    {booking.status === 'confirmed' && booking.paymentStatus === 'pending' && (
                      <div className="ui-mdc-booking-status-payment">
                        <h5 className="ui-mdc-booking-status-payment-title">💳 Payment Required</h5>
                        <p className="ui-mdc-booking-status-payment-text">
                          Your booking is confirmed! Our team will contact you shortly with payment details.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Help Section */}
        <div className="ui-mdc-booking-status-help">
          <h3 className="ui-mdc-booking-status-help-title">Need Help?</h3>
          <p className="ui-mdc-booking-status-help-text">
            If you have any questions about your booking, please contact us:
          </p>
          <div className="ui-mdc-booking-status-help-actions">
            <a 
              href="mailto:info@yalavibe.com"
              className="ui-mdc-booking-status-help-btn ui-mdc-booking-status-help-btn--email"
            >
              Email Us
            </a>
            <a 
              href="https://wa.me/94707238259" 
              className="ui-mdc-booking-status-help-btn ui-mdc-booking-status-help-btn--whatsapp"               
              target="_blank"
              rel="noopener noreferrer"
            >
               WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserBookingStatus;